// @ts-nocheck
import { useState, useEffect } from 'react'
import { X, Cloud, CloudOff, Loader2, Save } from 'lucide-react'
import { useApi } from '../hooks/useApi'

/**
 * 通話中のメモエディタコンポーネント
 * リアルタイムでメモを入力し、バックエンドに自動保存
 */
export default function InCallNoteEditor({
  value,
  onChange,
  tags = [],
  onTagsChange,
  callSid,
  organizationId,
  phoneNumber = null,
  autoSaveStatus = 'saved',
  autoSaveLastSaved = null,
  autoSaveError = null,
  onManualSave = null,
  isCallActive = true
}) {
  const [customTag, setCustomTag] = useState('')
  const [quickTags, setQuickTags] = useState([])
  const api = useApi()

  // 通話開始時に前回の下書きを復元（初回のみ）
  useEffect(() => {
    if (callSid) {
      const savedNote = localStorage.getItem(`call-note-${callSid}`)
      if (savedNote && !value) {
        onChange(savedNote)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSid])

  // メモが変更されたらローカルストレージにも保存（バックアップ用）
  useEffect(() => {
    if (value && callSid) {
      try {
        localStorage.setItem(`call-note-${callSid}`, value)
      } catch (err) {
        console.error('ローカルストレージへの保存エラー:', err)
      }
    }
  }, [value, callSid])

  // 電話番号オプションからクイックタグを取得
  useEffect(() => {
    const fetchQuickTags = async () => {
      console.log('[InCallNoteEditor] クイックタグ取得開始:', { phoneNumber, organizationId })
      
      if (!phoneNumber) {
        console.log('[InCallNoteEditor] 電話番号なし - localStorageから取得')
        // 電話番号がない場合はlocalStorageから取得（フォールバック）
        try {
          const savedTags = localStorage.getItem(`popular-tags-${organizationId}`)
          if (savedTags) {
            const parsedTags = JSON.parse(savedTags)
            const sortedTags = Object.entries(parsedTags)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([tag]) => tag)
            console.log('[InCallNoteEditor] localStorageから取得したタグ:', sortedTags)
            setQuickTags(sortedTags)
          }
        } catch (err) {
          console.error('[InCallNoteEditor] タグ取得エラー:', err)
        }
        return
      }

      try {
        // 電話番号オプションからクイックタグを取得
        console.log('[InCallNoteEditor] 電話番号オプションを取得中:', phoneNumber)
        const data = await api.get('/api/phone-numbers')
        console.log('[InCallNoteEditor] 取得した電話番号リスト:', data.items?.map(p => p.phoneNumber))
        const matchedPhone = data.items?.find(p => p.phoneNumber === phoneNumber)
        console.log('[InCallNoteEditor] マッチした電話番号:', matchedPhone)
        
        if (matchedPhone && matchedPhone.quickTags && matchedPhone.quickTags.length > 0) {
          // 電話番号に設定されたクイックタグを使用（labelのみを配列に）
          const tags = matchedPhone.quickTags.map(tag => tag.label || tag)
          console.log('[InCallNoteEditor] 電話番号設定から取得したタグ:', tags)
          setQuickTags(tags)
        } else {
          console.log('[InCallNoteEditor] 電話番号設定なし - localStorageから取得')
          // 設定がない場合はlocalStorageから取得
          const savedTags = localStorage.getItem(`popular-tags-${organizationId}`)
          if (savedTags) {
            const parsedTags = JSON.parse(savedTags)
            const sortedTags = Object.entries(parsedTags)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([tag]) => tag)
            console.log('[InCallNoteEditor] localStorageから取得したタグ:', sortedTags)
            setQuickTags(sortedTags)
          }
        }
      } catch (err) {
        console.error('[InCallNoteEditor] 電話番号オプション取得エラー:', err)
        // エラー時はlocalStorageから取得
        try {
          const savedTags = localStorage.getItem(`popular-tags-${organizationId}`)
          if (savedTags) {
            const parsedTags = JSON.parse(savedTags)
            const sortedTags = Object.entries(parsedTags)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([tag]) => tag)
            console.log('[InCallNoteEditor] エラー時 - localStorageから取得したタグ:', sortedTags)
            setQuickTags(sortedTags)
          }
        } catch (fallbackErr) {
          console.error('[InCallNoteEditor] フォールバックタグ取得エラー:', fallbackErr)
        }
      }
    }

    if (organizationId) {
      fetchQuickTags()
    }
  }, [organizationId, phoneNumber])

  // タグが追加されたらローカルストレージに使用頻度を保存
  useEffect(() => {
    if (tags.length > 0 && organizationId) {
      try {
        const savedTags = localStorage.getItem(`popular-tags-${organizationId}`)
        const tagCounts = savedTags ? JSON.parse(savedTags) : {}
        
        tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1
        })
        
        localStorage.setItem(`popular-tags-${organizationId}`, JSON.stringify(tagCounts))
      } catch (err) {
        console.error('タグ保存エラー:', err)
      }
    }
  }, [tags, organizationId])

  const formatRelativeTime = (date) => {
    if (!date) return ''
    const seconds = Math.floor((new Date() - date) / 1000)
    if (seconds < 10) return 'たった今'
    if (seconds < 60) return `${seconds}秒前`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}分前`
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
  }

  const addQuickTag = (tag) => {
    if (!tags.includes(tag)) {
      onTagsChange([...tags, tag])
    }
  }

  const addCustomTag = () => {
    if (!customTag.trim()) return
    
    const tag = customTag.trim()
    if (!tags.includes(tag)) {
      onTagsChange([...tags, tag])
    }
    setCustomTag('')
  }

  const removeTag = (tagToRemove) => {
    onTagsChange(tags.filter(t => t !== tagToRemove))
  }

  const handleCustomTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addCustomTag()
    }
  }

  const handleManualSave = async () => {
    if (onManualSave) {
      await onManualSave()
    }
  }

  return (
    <div>
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="通話内容をメモしてください...&#10;30秒ごとに自動保存されます"
          className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
        />
        
        {/* 手動保存ボタン（通話中のみ表示） */}
        {isCallActive && onManualSave && value && (
          <button
            onClick={handleManualSave}
            disabled={autoSaveStatus === 'saving'}
            className="absolute top-2 right-2 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="今すぐ保存"
          >
            <Save className="w-3.5 h-3.5" />
            保存
          </button>
        )}
      </div>
      
      {/* 保存状態表示 */}
      <div className="mt-1 flex items-center gap-2 text-xs">
        {autoSaveStatus === 'saving' && (
          <>
            <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
            <span className="text-blue-600 dark:text-blue-400">保存中...</span>
          </>
        )}
        {autoSaveStatus === 'saved' && autoSaveLastSaved && (
          <>
            <Cloud className="w-3 h-3 text-green-500" />
            <span className="text-green-600 dark:text-green-400">
              ✓ {formatRelativeTime(autoSaveLastSaved)}に自動保存
            </span>
          </>
        )}
        {autoSaveStatus === 'unsaved' && (
          <>
            <CloudOff className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">未保存の変更があります</span>
          </>
        )}
        {autoSaveStatus === 'error' && (
          <>
            <CloudOff className="w-3 h-3 text-red-500" />
            <span className="text-red-600 dark:text-red-400">
              保存エラー: {autoSaveError || '不明なエラー'}
            </span>
          </>
        )}
        {!value && (
          <span className="text-gray-500 dark:text-gray-400">
            メモを入力すると30秒ごとに自動保存されます
          </span>
        )}
      </div>

      {/* タグ管理 */}
      <div className="mt-3">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">タグ</div>
        
        {/* 追加されたタグ */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition"
                  title="削除"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* カスタムタグ入力 */}
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            onKeyPress={handleCustomTagKeyPress}
            placeholder="新しいタグを入力..."
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <button
            onClick={addCustomTag}
            disabled={!customTag.trim()}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            追加
          </button>
        </div>

        {/* クイックタグ */}
        {quickTags.length > 0 && (
          <>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">よく使うタグ:</div>
            <div className="flex flex-wrap gap-1">
              {quickTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => addQuickTag(tag)}
                  disabled={tags.includes(tag)}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + {tag}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
